/**
 * Pure chess.js helpers for the analysis branch (fix F-C / C1).
 *
 * `analysisStore.ts` holds the branch's STATE but knows nothing about chess
 * rules; everything that has to understand a position lives here instead, so
 * it can be unit-tested without a store. No store reads — every caller passes
 * the FEN it means.
 */
import {Chess} from 'chess.js';

import type {BranchMove, TerminalVerdict} from './analysisStore';

/**
 * `from` → `to` (+ promotion, default queen — the board only offers legal
 * moves, so a drag-drop never needs to ask) on `fen`. Null when the move is
 * illegal there, or `fen` itself cannot be loaded.
 */
export const applyMove = (
  fen: string,
  from: string,
  to: string,
  promotion?: string,
): BranchMove | null => {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    return null;
  }
  try {
    const move = game.move({from, to, promotion: promotion || 'q'});
    if (!move) {
      return null;
    }
    return {san: move.san, fen: game.fen(), from: move.from, to: move.to};
  } catch {
    return null;
  }
};

/**
 * The SAN played on `fen` — an `EngineLines` row's first move, pressed. Null
 * when it is not a legal move there (it always should be: the line came from
 * a search of this very position).
 */
export const sanToMove = (fen: string, san: string): BranchMove | null => {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    return null;
  }
  try {
    const move = game.move(san);
    if (!move) {
      return null;
    }
    return {san: move.san, fen: game.fen(), from: move.from, to: move.to};
  } catch {
    return null;
  }
};

/** true when `san` is the main line's OWN next move at `basePly`. */
export const isMainlineNext = (
  san: string,
  moves: string[],
  basePly: number,
): boolean => moves[basePly] === san;

/**
 * fix U: is `fen` a position the game has already ended in — and how.
 * `analysisStore.setPosition` and `useEngine` both call this on the SAME
 * fen (never the engine): Stockfish answers a mated/stalemated position
 * with `bestmove (none)` and no usable score, so the client decides whether
 * a position is even worth asking before it ever reaches the worker.
 *
 * Checked in this order because a position can satisfy more than one (bare
 * kings past move 50 is also insufficient material — a lone PAWN is not: it
 * can still promote): the two "no legal moves" cases first, then the
 * material/history draws, then the clock — chess.js keeps the halfmove
 * count private, so the 50-move rule is read straight off the FEN's own
 * field instead.
 *
 * Null for an ordinary, still-playable position, or a FEN that cannot be
 * loaded (same "never throws" contract as `applyMove`/`sanToMove` above).
 */
export const terminalVerdict = (fen: string): TerminalVerdict | null => {
  let game: Chess;
  try {
    game = new Chess(fen);
  } catch {
    return null;
  }
  if (game.isCheckmate()) {
    // the side TO MOVE is the one checkmated, so the OTHER side won
    return {kind: 'checkmate', winner: game.turn() === 'w' ? 'b' : 'w'};
  }
  if (game.isStalemate()) {
    return {kind: 'stalemate'};
  }
  if (game.isInsufficientMaterial()) {
    return {kind: 'insufficient'};
  }
  if (game.isThreefoldRepetition()) {
    return {kind: 'repetition'};
  }
  const halfMoveClock = Number(fen.trim().split(/\s+/)[4]);
  if (Number.isFinite(halfMoveClock) && halfMoveClock >= 100) {
    return {kind: 'fifty-move'};
  }
  return null;
};

export default applyMove;
